import { useMutation, useQuery, useQueryClient } from "react-query";

import { DateTime } from "@bps/date-time";
import { HttpError } from "@bps/http-client";
import { useGateways } from "@libs/api/gateways-context";
import { CurrentSiteVersion } from "@libs/api/gateways/sia/models/CurrentSiteVersion";
import { SiteComponentLogDto } from "@libs/api/gateways/sia/models/SiteComponentLog";
import {
  GetTenantsParams,
  LogLevels,
  ProsDatabaseCommands,
  ProsTenant,
  ReportsType,
  SIATenantDetails,
  SIATenantLicenceCheckRequest,
  SIATenantLicenceCheckResponse,
  SiteAvailableReport,
  SiteComponentState,
  SiteComponentType,
  SiteServerReport
} from "@libs/api/gateways/sia/sia-ops-gateway.dtos";
import { PremSettingsUpdate } from "@libs/api/gateways/tg/tg-ops-gateway.dtos";
import { guid } from "@libs/common/guid";

export const SiaCacheKeys = {
  AvailableComponentVersions: "pros-available-component-versions",
  SiteAvailableReports: "pros-site-available-reports",
  SiteComponentVersions: "pros-site-component-versions",
  ComponentVersions: "pros-component-versions",
  SiteVersions: "pros-site-versions",
  SiteLogs: "pros-site-logs",
  SiteServerReport: "pros-site-server-report",
  SiteManagerInstallerDetail: "site-manager-installer-detail",
  PremSetting: "pros-prem-settings",
  SIATenant: "sia-tenant",
  ApprovedComponentVersions: "approved-component-versions"
};
interface SiteArgs {
  siteId: guid;
}

export interface SiteDatabaseCommandArgs extends SiteArgs {
  command: ProsDatabaseCommands;
}

export interface PremSettingsArgs extends SiteArgs {
  updates: PremSettingsUpdate[];
}

export interface SiteComponentArgs extends SiteArgs {
  componentType: SiteComponentType;
}

export interface SiteCommandUpdateArgs {
  tenantId: string;
  commandType: string;
  subCommandType?: string;
  componentType: string;
  scheduled?: DateTime;
}

export interface SiteComponentLogLevelArgs extends SiteComponentArgs {
  logLevel: LogLevels;
}

export interface SiteComponentStateArgs extends SiteComponentArgs {
  state: SiteComponentState;
}

export interface SiteComponentVersionArgs extends SiteArgs {
  component: SiteComponentType;
  version: string;
}

// ----- QUERIES ---------------------------------------------------------------
export const useSiteActivity = (siteId: guid) => {
  const { siaOpsGateway } = useGateways();

  return useQuery<string>(
    [SiaCacheKeys.AvailableComponentVersions, siteId],
    async () => {
      return await siaOpsGateway.getSiteActivity(siteId);
    }
  );
};

export const useLogFilesQuery = (siteId: guid) => {
  const { siaOpsGateway } = useGateways();

  return useQuery<SiteComponentLogDto[]>(
    [SiaCacheKeys.SiteLogs, siteId],
    async () => await siaOpsGateway.getSiteLogs(siteId)
  );
};

export const useSiteCommandUpdateMutation = () => {
  const { siaOpsGateway } = useGateways();

  return useMutation<string, Error, SiteCommandUpdateArgs>(
    ({ tenantId, commandType, componentType, subCommandType, scheduled }) =>
      siaOpsGateway.postSiteCommandUpdate({
        tenantId,
        commandType,
        componentType,
        subCommandType,
        scheduled
      })
  );
};

export const useSiteAvailableReportsQuery = (
  siteId: guid,
  component: SiteComponentType,
  options: { type: ReportsType; forceRefresh?: boolean }
) => {
  const { type, forceRefresh } = options;
  const { siaOpsGateway } = useGateways();
  const queryClient = useQueryClient();

  if (forceRefresh) {
    queryClient.removeQueries([SiaCacheKeys.SiteAvailableReports, siteId]);
  }

  return useQuery<SiteAvailableReport[]>(
    [SiaCacheKeys.SiteAvailableReports, siteId, component, type],
    async () =>
      await siaOpsGateway.getSiteAvailableReports(siteId, component, type)
  );
};

export const useSiteReportQuery = (
  siteId: guid,
  component: SiteComponentType,
  options: { report: string; enabled?: boolean }
) => {
  const { report, enabled = true } = options;
  const { siaOpsGateway } = useGateways();

  return useQuery<SiteServerReport>(
    [SiaCacheKeys.SiteServerReport, siteId, report],
    async () => await siaOpsGateway.getSiteReport(siteId, component, report),
    {
      enabled
    }
  );
};

export const useTenantsQuery = (params?: GetTenantsParams) => {
  const { siaOpsGateway } = useGateways();
  return useQuery<ProsTenant[]>(
    [SiaCacheKeys.SiteLogs, params],
    async () => await siaOpsGateway.getTenants(params)
  );
};

export const useSIATenantQuery = (tenantId: guid) => {
  const { siaOpsGateway } = useGateways();
  return useQuery<SIATenantDetails>(
    [SiaCacheKeys.SIATenant, tenantId],
    async () => await siaOpsGateway.getTenant(tenantId)
  );
};

export const useSIATenantLicenceCheckQuery = () => {
  const { siaOpsGateway } = useGateways();
  return useMutation<
    SIATenantLicenceCheckResponse,
    HttpError,
    SIATenantLicenceCheckRequest
  >(siaOpsGateway.checkTenantLicence);
};

// ----- MUTATIONS WITH CACHE EXPIRY -------------------------------------------
export const useLogFilesUploadMutation = () => {
  const { siaOpsGateway } = useGateways();
  const queryClient = useQueryClient();

  return useMutation<string, Error, SiteComponentArgs>(
    ({ siteId, componentType }) =>
      siaOpsGateway.uploadLogs(siteId, componentType),
    {
      onSuccess: async (response: string, { siteId }: SiteComponentArgs) => {
        await queryClient.invalidateQueries([SiaCacheKeys.SiteLogs, siteId]);
      }
    }
  );
};

// ----- PLAIN MUTATIONS -------------------------------------------------------
export const useHealthCheckMutation = () => {
  const { siaOpsGateway } = useGateways();

  return useMutation<string, Error, string>(siteId =>
    siaOpsGateway.getHealthCheck(siteId)
  );
};

export const usePingMutation = () => {
  const { siaOpsGateway } = useGateways();

  return useMutation<string, Error, SiteComponentArgs>(
    ({ siteId, componentType }) => siaOpsGateway.getPing(siteId, componentType)
  );
};

export const useLogLevelUpdateMutation = () => {
  const { siaOpsGateway } = useGateways();

  return useMutation<string, Error, SiteComponentLogLevelArgs>(
    async ({ siteId, componentType, logLevel }) => {
      return await siaOpsGateway.updateLogLevel(
        logLevel.toString(),
        siteId,
        componentType
      );
    }
  );
};

export const useComponentStateUpdateMutation = () => {
  const { siaOpsGateway } = useGateways();
  const queryClient = useQueryClient();
  return useMutation<string, Error, SiteComponentStateArgs>(
    ({ siteId, state, componentType }) => {
      return siaOpsGateway.updateComponentState(siteId, state, componentType);
    },
    {
      onSuccess: async (
        response: string,
        { siteId, state, componentType }: SiteComponentStateArgs
      ) => {
        if (state === SiteComponentState.Update) {
          queryClient.setQueryData<CurrentSiteVersion[]>(
            [SiaCacheKeys.SiteComponentVersions, siteId],
            prev => {
              if (!prev) return [];
              return prev.map(currentVersion => {
                if (currentVersion.componentType !== componentType) {
                  return currentVersion;
                }
                return new CurrentSiteVersion({
                  component: componentType,
                  version: currentVersion.desiredVersion,
                  desiredVersion: currentVersion.desiredVersion
                });
              });
            }
          );
        }

        queryClient.removeQueries([SiaCacheKeys.SiteVersions, siteId]);
        return;
      },
      onError: e => {
        throw e;
      }
    }
  );
};

export const useModifyDatabaseMutation = () => {
  const { siaOpsGateway } = useGateways();

  return useMutation<string, Error, SiteDatabaseCommandArgs>(
    ({ siteId, command }) => siaOpsGateway.modifyPrOSDatabase(siteId, command)
  );
};

export const useProsBannerMutation = () => {
  // @TODO  Replace when api ready
  return useMutation<string, Error, string>(() => Promise.resolve(""));
};

export const useDeleteEntityEventsMutation = () => {
  const { siaOpsGateway } = useGateways();

  return useMutation<string, Error, string>(siteId =>
    siaOpsGateway.deleteEntityEvents(siteId)
  );
};

export const useSiteComponentCredentialMutation = () => {
  const { siaOpsGateway } = useGateways();

  return useMutation<string, Error, string>(tenantId =>
    siaOpsGateway.updateSiteComponentCredentials(tenantId)
  );
};
